import * as React from 'react'

import type { ILocationWorkflowRestrictions } from '~/client/graph'
import type { ProjectResourceOperationFailure } from '~/client/src/shared/resources/domain/projectResourceType'
import type {
  IProposedResourceControls,
  IResourceControlCommands,
  IResourceControlsUpdate,
  IWorkflowRestrictionDraft,
} from '~/client/src/shared/resources/domain/resourceControls'
import type { ResourceResult } from '~/client/src/shared/resources/domain/resourceResult'
import type IMsDateInterval from '~/client/src/shared/types/IMsDateInterval'

/**
 * The location control fields the shared Controls tab reads from an
 * `IResourceRecord.root` projection.
 */
export interface IControlFields {
  readonly permittedCompanies: string[]
  readonly isBookable: boolean
  readonly showInMap: boolean
  readonly workflowRestrictions: ILocationWorkflowRestrictions | null
  readonly operatingIntervals: IMsDateInterval[]
}

const isDateInterval = (value: unknown): value is IMsDateInterval => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  return (
    'startDate' in value &&
    typeof value.startDate === 'number' &&
    (!('endDate' in value) ||
      value.endDate === undefined ||
      typeof value.endDate === 'number') &&
    (!('isDaily' in value) ||
      value.isDaily === undefined ||
      typeof value.isDaily === 'boolean')
  )
}

const isWorkflowRestrictions = (
  value: unknown,
): value is ILocationWorkflowRestrictions => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const durations =
    'maxBookingDurations' in value ? value.maxBookingDurations : undefined
  const overlaps =
    'maxOverlappingBookings' in value ? value.maxOverlappingBookings : undefined
  return (
    (durations === undefined ||
      durations === null ||
      Array.isArray(durations)) &&
    (overlaps === undefined || overlaps === null || Array.isArray(overlaps))
  )
}

/** Extracts the complete control projection consumed by resource controls. */
export function extractControlFields(
  loc: Record<string, unknown>,
): IControlFields {
  const workflowRestrictions = loc.workflowRestrictions
  return {
    permittedCompanies:
      'permittedCompanies' in loc && Array.isArray(loc.permittedCompanies)
        ? loc.permittedCompanies.flatMap(value =>
            value === null || value === undefined ? [] : [String(value)],
          )
        : [],
    isBookable:
      'isBookable' in loc && typeof loc.isBookable === 'boolean'
        ? loc.isBookable
        : false,
    showInMap:
      'showInMap' in loc && typeof loc.showInMap === 'boolean'
        ? loc.showInMap
        : true,
    workflowRestrictions:
      workflowRestrictions === null ||
      isWorkflowRestrictions(workflowRestrictions)
        ? workflowRestrictions
        : null,
    operatingIntervals:
      'operatingIntervals' in loc && Array.isArray(loc.operatingIntervals)
        ? loc.operatingIntervals.filter(isDateInterval)
        : [],
  }
}

const requireControlSuccess = <T>(
  result: ResourceResult<T, ProjectResourceOperationFailure>,
): T => {
  if (result._tag === 'Ok') return result.value
  if (result.error.cause instanceof Error) throw result.error.cause
  throw new Error(
    `Resource control operation failed: ${result.error.operation}`,
  )
}

/**
 * Binds one project-scoped resource store's control commands to React callers.
 * The store owns every GraphQL effect; this hook only translates typed failures
 * at the presentation seam.
 */
export function useResourceControls(
  projectId: string | undefined,
  commands: IResourceControlCommands,
) {
  const execute = React.useCallback(
    async <T>(
      operation: Promise<ResourceResult<T, ProjectResourceOperationFailure>>,
    ): Promise<T> => requireControlSuccess(await operation),
    [],
  )

  const requireProjectId = React.useCallback((): string => {
    if (!projectId) throw new Error('Missing projectId')
    return projectId
  }, [projectId])

  const savePermittedCompanies = React.useCallback(
    (recordIds: string[], companyIds: string[]): Promise<void> =>
      execute(
        commands.savePermittedCompanies(
          requireProjectId(),
          recordIds,
          companyIds,
        ),
      ),
    [commands, execute, requireProjectId],
  )

  const saveBookable = React.useCallback(
    (recordIds: string[], isBookable: boolean): Promise<void> =>
      execute(commands.saveBookable(requireProjectId(), recordIds, isBookable)),
    [commands, execute, requireProjectId],
  )

  const saveShowInMap = React.useCallback(
    (recordIds: string[], showInMap: boolean): Promise<void> =>
      execute(commands.saveShowInMap(requireProjectId(), recordIds, showInMap)),
    [commands, execute, requireProjectId],
  )

  const saveOperatingIntervals = React.useCallback(
    (recordIds: string[], intervals: IMsDateInterval[]): Promise<void> =>
      execute(
        commands.saveOperatingIntervals(
          requireProjectId(),
          recordIds,
          intervals,
        ),
      ),
    [commands, execute, requireProjectId],
  )

  const saveWorkflowRestrictions = React.useCallback(
    (
      recordIds: string[],
      restrictions: IWorkflowRestrictionDraft[],
    ): Promise<readonly string[]> =>
      execute(
        commands.saveWorkflowRestrictions(
          requireProjectId(),
          recordIds,
          restrictions,
        ),
      ),
    [commands, execute, requireProjectId],
  )

  const disableAndClearControls = React.useCallback(
    (recordIds: string[]) =>
      execute(commands.disableAndClearControls(requireProjectId(), recordIds)),
    [commands, execute, requireProjectId],
  )

  const previewControlCascade = React.useCallback(
    (recordIds: string[], proposed: IProposedResourceControls) =>
      execute(
        commands.previewControlCascade(requireProjectId(), recordIds, proposed),
      ),
    [commands, execute, requireProjectId],
  )

  const saveControls = React.useCallback(
    (recordIds: string[], update: IResourceControlsUpdate) =>
      execute(commands.saveControls(requireProjectId(), recordIds, update)),
    [commands, execute, requireProjectId],
  )

  return {
    savePermittedCompanies,
    saveBookable,
    saveShowInMap,
    saveOperatingIntervals,
    saveWorkflowRestrictions,
    disableAndClearControls,
    previewControlCascade,
    saveControls,
  }
}
